import React, { memo, useRef, useState } from "react";
import { motion, MotionValue, useMotionValueEvent } from "framer-motion";
import Image from "next/image";
import { WeatherForecastData } from "@/types/forecast";
import { TFunction } from "i18next";
import "@/app/globals.css";
import { getPrecipitation } from "@/app/(pages)/Home/components/mobile/MainWeatherDisplay/features/getPrecipitation";
import {
  getDetailedIconName,
  getWeatherIcon,
  getWeatherIconName,
  weatherIconCorrection,
} from "@/utils/getWeatherIcon";
import cx from "clsx";
import WeatherPrecipitation from "@/app/(pages)/Home/components/mobile/MainWeatherDisplay/components/WeatherPrecipitation";
import AllowGeolocationPanel from "@/app/(components)/AllowGeolocationPanel";
import {
  getDayDisplayCode,
  getSegmentCodeAtHour,
} from "@/app/(pages)/Home/components/shared/Highcharts/components/WeatherTimeline";
import { getCityClockFromDay } from "@/utils/cityTime";
import { isNightForDay } from "@/utils/dayNight";

type MainWeatherDisplayProps = {
  mainWeatherOpacity: MotionValue<number>;
  mainWeatherDisplay: MotionValue<string>;
  panelHeaderOpacity: MotionValue<number>;
  panelHeaderDisplay: MotionValue<string>;
  backgroundOverlayOpacity: MotionValue<number>;
  city: string;
  weather: WeatherForecastData;
  activeDay: string;
  currentHour: number;
  t: TFunction<"translation", undefined>;
  isFirstEnter: boolean;
};

const MainWeatherDisplay = ({
  mainWeatherOpacity,
  mainWeatherDisplay,
  panelHeaderOpacity,
  panelHeaderDisplay,
  backgroundOverlayOpacity,
  city,
  weather,
  activeDay,
  currentHour,
  t,
  isFirstEnter,
}: MainWeatherDisplayProps) => {
  const day = weather[activeDay];
  const isToday = activeDay === getCityClockFromDay(day).todayKey;
  const weatherCode = isToday
    ? (getSegmentCodeAtHour(day, currentHour) ?? day.weatherCode)
    : (getDayDisplayCode(day) ?? day.weatherCode);
  const precip = getPrecipitation(weatherIconCorrection(weatherCode).code);
  const weatherIcon = getWeatherIcon(weatherCode);
  const isNight = isNightForDay(day);

  const cloudRef = useRef<HTMLImageElement>(null);
  const cityRef = useRef<HTMLHeadingElement>(null);

  const [sceneHidden, setSceneHidden] = useState(false);
  const sceneHiddenRef = useRef(false);
  useMotionValueEvent(backgroundOverlayOpacity, "change", (v) => {
    const next = sceneHiddenRef.current ? v > 0.95 : v > 0.98;
    if (next !== sceneHiddenRef.current) {
      sceneHiddenRef.current = next;
      setSceneHidden(next);
    }
  });

  const { sun } = getDetailedIconName(weatherCode, isNight);

  return (
    <>
      <motion.section
        ref={cityRef}
        style={{
          opacity: mainWeatherOpacity,
          display: mainWeatherDisplay,
        }}
        className="mobile-hero-spacing wsm:hidden relative w-full flex-col items-center justify-around font-[inter] will-change-[opacity]"
      >
        <AllowGeolocationPanel
          isFirstEnter={isFirstEnter}
          isMobile={true}
          ref={cityRef}
        />
        <h2 className="vp:location w-fit">{city}</h2>
        <h1 className="vp:temperature-main w-fit font-thin">
          {Math.floor(weather[activeDay].temperature[currentHour])}°
        </h1>
        <sub className="vp:description-main inline-grid grid-cols-2 gap-x-2 font-semibold text-shadow-[1px_2px_5px_black]">
          <p className="col-span-2 w-full text-center text-white/60">
            {t(`weather.${weatherCode}.description`)}
          </p>

          <span className="text-right">
            В:&nbsp;{Math.floor(weather[activeDay].maxTemperature)}°
          </span>
          <span>Н:&nbsp;{Math.floor(weather[activeDay].minTemperature)}°</span>
        </sub>
      </motion.section>
      <motion.section
        style={{
          opacity: panelHeaderOpacity,
          display: panelHeaderDisplay,
        }}
        className="wsm:block wsm:opacity-100 wsm:top-2 absolute top-8 flex-col items-center font-[inter] will-change-[opacity]"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-1/2 -z-1 h-28 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#8b5cf6]/30 blur-[60px]"
        />
        <h2 className="relative flex flex-col items-center">
          <p className="text-3xl">{city}</p>
          <span className="text-base">
            {Math.floor(weather[activeDay].temperature[currentHour])}
            °&nbsp;·&nbsp;
            {t(`weather.${weatherCode}.description`)}
          </span>
        </h2>
      </motion.section>
      <div
        className={cx("mobile-house", sceneHidden && "scene-paused")}
        style={{ visibility: sceneHidden ? "hidden" : undefined }}
      >
        <Image
          src="/shared/house.mobile.webp"
          alt=""
          fill
          priority
          sizes="(min-width: 560px) 560px, 100vmin"
          aria-hidden
          className="relative -z-2 object-contain"
        />
        <WeatherPrecipitation
          precip={precip}
          emitterRef={cloudRef}
          className="-z-2"
          paused={sceneHidden}
        />
        <Image
          ref={cloudRef}
          src={`/weather/${getWeatherIconName(weatherCode, isNight)}.png`}
          alt={t(`weather.${weatherCode}.description`)}
          width="160"
          height="140"
          loading="lazy"
          className={cx(
            "cloud-drift absolute -top-1/4 right-0 -z-1 h-auto",
            weatherCode === 0
              ? isNight
                ? "w-[33%]"
                : "icon-sun w-[33%]"
              : "w-[41%] translate-y-full",
          )}
        />
        <Image
          src={`/weather/${isNight ? "0.night" : "0"}.png`}
          alt=""
          width="128"
          height="128"
          loading="lazy"
          className={cx(
            "cloud-drift absolute -top-6/16 right-0 -z-2 h-auto !-rotate-5",
            weatherCode === 0 ? (isNight ? "" : "icon-sun w-[33%]") : "",
            !sun && "hidden",
          )}
        />
        {weatherCode === 3 && (
          <Image
            src={`/weather/${weatherIcon}.png`}
            alt=""
            aria-hidden
            width="160"
            height="140"
            loading="lazy"
            className={cx(
              "cloud-back absolute -top-4/12 right-0 -z-2 h-auto blur-[2px] brightness-80",
              "w-[37%]",
            )}
          />
        )}
        <motion.div
          style={{ opacity: mainWeatherOpacity }}
          className="absolute bottom-0 left-1/2 h-4 w-8/12 -translate-x-1/2 bg-white/40 blur-lg will-change-[opacity]"
        />
      </div>
      <motion.div
        style={{ opacity: backgroundOverlayOpacity }}
        className="from-deep-indigo to-charcoal-indigo absolute inset-0 -z-1 h-screen w-screen bg-linear-145 will-change-[opacity]"
      />
      <motion.div
        className="bg-soft-purple -translate-panelY-1/2 absolute left-1/2 z-5 h-1 w-8/12 -translate-x-1/2 rounded-full blur-lg brightness-115 will-change-[opacity]"
        style={{
          top: "var(--pull-up, 6.75rem)",
          opacity: backgroundOverlayOpacity,
        }}
      />
    </>
  );
};

export default memo(MainWeatherDisplay);
